import type { CustomerPasskeyCredential } from '../../types'
import type { ShopMessageKey } from '../../../i18n'
import { CustomerAreaSurfaceCard } from '../customer-area'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type PasskeySettingsPanelProps = {
  t: TranslateFn
  passkeys: CustomerPasskeyCredential[]
  loading: boolean
  error: string
  success: string
  removingId: string | null
  supported: boolean
  twoFactorEnabled: boolean
  onAddPasskey: () => void
  onRemovePasskey: (passkeyId: string) => void
}

const formatPasskeyDate = (value: string | null): string => {
  if (!value) return '--'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '--'
  return date.toLocaleString()
}

export const PasskeySettingsPanel = ({
  t,
  passkeys,
  loading,
  error,
  success,
  removingId,
  supported,
  twoFactorEnabled,
  onAddPasskey,
  onRemovePasskey,
}: PasskeySettingsPanelProps) => (
  <CustomerAreaSurfaceCard
    title={t('customer.profile.passkeys.title')}
    subtitle={t('customer.profile.passkeys.subtitle')}
    actions={(
      <button
        type="button"
        onClick={onAddPasskey}
        disabled={loading || !supported || !twoFactorEnabled}
        className="ca-btn-primary inline-flex items-center px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? t('customer.profile.passkeys.loading') : t('customer.profile.passkeys.add')}
      </button>
    )}
  >
    {!twoFactorEnabled ? (
      <p className="text-xs text-amber-700">{t('customer.profile.passkeys.requiresTwoFactor')}</p>
    ) : null}
    {!supported ? <p className="text-xs text-amber-700">{t('customer.profile.passkeys.notSupported')}</p> : null}
    {error ? <p className="text-xs text-(--color-status-error)">{error}</p> : null}
    {success ? <p className="text-xs text-emerald-700">{success}</p> : null}

    {passkeys.length === 0 ? (
      <div className="rounded-2xl border border-(--color-border-default) bg-(--color-bg-surface-muted) px-4 py-3 text-sm text-(--color-text-secondary)">
        {t('customer.profile.passkeys.empty')}
      </div>
    ) : (
      <ul className="space-y-2">
        {passkeys.map((passkey, index) => (
          <li
            key={passkey.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-(--color-border-default) bg-(--color-bg-surface-muted) px-4 py-3"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-(--color-text-primary)">
                {passkey.nickname || t('customer.profile.passkeys.deviceDefault', { count: index + 1 })}
              </p>
              <p className="text-xs text-(--color-text-muted)">
                {t('customer.profile.passkeys.createdAt', { date: formatPasskeyDate(passkey.createdAt) })}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onRemovePasskey(passkey.id)}
              disabled={loading || removingId === passkey.id}
              className="ca-btn-secondary inline-flex items-center px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            >
              {removingId === passkey.id
                ? t('customer.profile.passkeys.removing')
                : t('customer.profile.passkeys.remove')}
            </button>
          </li>
        ))}
      </ul>
    )}
  </CustomerAreaSurfaceCard>
)
